import React, { useRef, useEffect, useMemo, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

function VideoBackground() {
  const meshRef = useRef()
  const videoRef = useRef()
  const [currentOpacity, setCurrentOpacity] = useState(0.10)
  const [targetOpacity, setTargetOpacity] = useState(0.10)
  const [lastPulseTime, setLastPulseTime] = useState(0)
  
  // Create video element and texture
  const { videoTexture, video } = useMemo(() => {
    const video = document.createElement('video')
    // video.crossOrigin = 'Anonymous' // Remove CORS which can cause issues
    video.loop = true
    video.muted = true
    video.playsInline = true
    video.preload = 'metadata'
    
    // Use the web-optimized video with baseline profile
    const sources = [
      { src: '/vids/rarevid1_web.mp4', type: 'video/mp4; codecs="avc1.42E01E"' },
      { src: '/vids/rarevid1.mp4', type: 'video/mp4' }, // Fallback to original
    ]
    
    sources.forEach(({ src, type }) => {
      const source = document.createElement('source')
      source.src = src
      source.type = type
      video.appendChild(source)
    })
    
    const texture = new THREE.VideoTexture(video)
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.format = THREE.RGBFormat
    
    return { videoTexture: texture, video }
  }, [])
  
  // Store video ref for cleanup
  useEffect(() => {
    videoRef.current = video
    
    // Add event listeners for debugging
    video.addEventListener('loadstart', () => console.log('Video texture: loadstart'))
    video.addEventListener('canplay', () => console.log('Video texture: canplay'))
    video.addEventListener('playing', () => console.log('Video texture: playing'))
    video.addEventListener('error', (e) => console.error('Video texture error:', e))
    
    // Start playing the video
    const playVideo = async () => {
      try {
        console.log('Attempting to play video texture...')
        await video.play()
        console.log('Video texture is playing successfully')
      } catch (error) {
        console.error('Error playing video texture:', error)
        console.log('Video texture will be disabled due to playback issues')
        // Set opacity to 0 if video fails to play
        setCurrentOpacity(0)
        setTargetOpacity(0)
      }
    }
    
    // Add a delay to ensure the video element and sources are ready
    setTimeout(playVideo, 500)
    
    return () => {
      video.pause()
      video.src = ''
    }
  }, [video])
  
  // Update texture and handle opacity animation on each frame
  useFrame((state) => {
    if (videoTexture) {
      videoTexture.needsUpdate = true
    }
    
    // Handle random opacity pulses
    const currentTime = state.clock.elapsedTime
    
    // Check if it's time for a random pulse (every 10-15 seconds randomly)
    if (currentTime - lastPulseTime > 10 + Math.random() * 5) {
      setTargetOpacity(0.4) // Pulse to 40%
      setLastPulseTime(currentTime)
      
      // After 2 seconds, fade back to 10%
      setTimeout(() => {
        setTargetOpacity(0.10)
      }, 2000)
    }
    
    // Smoothly animate towards target opacity
    const lerpSpeed = 0.02 // Smooth transition speed
    setCurrentOpacity(prev => {
      const diff = targetOpacity - prev
      if (Math.abs(diff) < 0.001) return targetOpacity
      return prev + diff * lerpSpeed
    })
    
    // Update material opacity
    if (meshRef.current && meshRef.current.material) {
      meshRef.current.material.opacity = currentOpacity
    }
  })
  
  return (
    <mesh ref={meshRef} position={[0, 0, -8]} scale={[18.4, 13.8, 1]}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial 
        map={videoTexture} 
        transparent 
        opacity={currentOpacity}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  )
}

export default VideoBackground
